/**
 * Avatar gesture/emote controller for the Sumerian Host.
 *
 * Sumerian Hosts ship a GestureFeature (hand gestures) and an emote animation
 * layer. The exact layer names vary, so we introspect the host's
 * AnimationFeature at runtime and match layers by name. All calls are
 * best-effort (wrapped in try/catch) so a missing clip never breaks the demo.
 */
export class AvatarAnimation {
  constructor(host) {
    this.host = host;
    this.lastGesture = 0;
    this.lastEmote = 0;
    this.gestureLayer = null;
    this.gestureNames = [];
    this.emoteLayer = null;
    this.emoteNames = [];
    this._discover();
  }

  _discover() {
    try {
      const af = this.host && this.host.AnimationFeature;
      if (!af) return;
      const layers = af.layers || [];
      console.log("[avatar-anim] animation layers:", layers);
      for (const ln of layers) {
        let names = [];
        try { names = af.getAnimations(ln) || []; } catch (e) { /* ignore */ }
        console.log(`[avatar-anim] layer "${ln}":`, names);
        const low = String(ln).toLowerCase();
        if (low.includes("gesture")) { this.gestureLayer = ln; this.gestureNames = names; }
        if (low.includes("emote")) { this.emoteLayer = ln; this.emoteNames = names; }
      }
    } catch (e) {
      console.warn("[avatar-anim] discovery failed:", e);
    }
  }

  /** Play a natural conversational gesture while Sam is talking (throttled). */
  playTalkingGesture() {
    const now = Date.now();
    if (now - this.lastGesture < 3500) return;
    this.lastGesture = now;
    const prefer = ["generic_a", "generic_b", "generic_c", "big", "one",
                    "you", "many", "movement", "in", "self", "aggressive"];
    const pool = this.gestureNames.length
      ? this.gestureNames.filter((n) => prefer.includes(n) || /generic/i.test(n))
      : prefer;
    const list = pool.length ? pool : this.gestureNames;
    if (!list.length) return;
    this._gesture(list[Math.floor(Math.random() * list.length)]);
  }

  /** Celebratory reaction for goals / wins (emote if available, else big gesture). */
  celebrate() {
    const now = Date.now();
    if (now - this.lastEmote < 4000) return;
    this.lastEmote = now;
    const emo = (this.emoteNames || []).find((n) => /cheer|applau|celebr|win|yes|excite/i.test(n));
    if (emo && this.emoteLayer) { this._emote(emo); return; }
    // fallback to an emphatic gesture
    this._gesture(this.gestureNames.includes("big") ? "big"
                  : this.gestureNames.includes("heart") ? "heart"
                  : (this.gestureNames[0] || null));
  }

  /** A thinking/considering beat (for questions). */
  think() {
    this._gesture(this.gestureNames.includes("one") ? "one"
                  : (this.gestureNames.find((n) => /generic/i.test(n)) || null));
  }

  _gesture(name) {
    if (!name) return;
    try {
      if (this.host.GestureFeature && this.gestureLayer) {
        this.host.GestureFeature.playGesture(this.gestureLayer, name, {
          transitionTime: 0.4, holdTime: 1.5,
        });
      }
    } catch (e) { /* best effort */ }
  }

  _emote(name) {
    if (!name) return;
    try {
      this.host.AnimationFeature.playAnimation(this.emoteLayer, name, 0.4);
    } catch (e) { /* best effort */ }
  }
}
